import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, ChevronLeft, ExternalLink, Loader2, ShieldCheck, ShoppingBag, Crosshair, CalendarDays, MapPin, Layers } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { PolicyBar } from "@/components/PolicyBar";
import { SPECIES_PHOTO, SPECIES_PHOTO_FALLBACK } from "@/lib/stateMeta";

const HUNT_TYPE = { general: "General", controlled: "Controlled", otc: "OTC" };
const val = (v) => (v == null ? "—" : v);

const OverviewStat = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span className="w-10 h-10 rounded-full bg-blaze/10 text-blaze grid place-items-center shrink-0"><Icon size={18} /></span>
    <div>
      <p className="font-serif text-2xl text-charcoal leading-none tabular-nums">{value}</p>
      <p className="text-[11px] uppercase tracking-wide text-charcoal/50 mt-1">{label}</p>
    </div>
  </div>
);

const Metric = ({ label, value, accent, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.12em] text-charcoal/45">{label}</p>
    <p className={`font-serif leading-none tabular-nums mt-1.5 ${accent ? "text-blaze text-2xl" : "text-charcoal text-2xl"}`}>{value}</p>
  </div>
);

export default function SpeciesPage() {
  const { state, name } = useParams();
  const species = decodeURIComponent(name);
  const [info, setInfo] = useState(null);
  const [hunts, setHunts] = useState(null);
  const { setAiContext } = useUI();

  useEffect(() => {
    setAiContext({ state, species, page: "species" });
    return () => setAiContext(null);
  }, [state, species, setAiContext]);

  useEffect(() => {
    setInfo(null); setHunts(null);
    api.exploreSpecies(state).then(setInfo).catch(() => setInfo({ system: {}, official_source: null }));
    api.exploreHunts(state, { species }).then((d) => setHunts(d.hunts)).catch(() => setHunts([]));
  }, [state, species]);

  const source = info?.official_source;
  const system = info?.system;
  const weapons = useMemo(() => [...new Set((hunts || []).map((h) => h.weapon).filter(Boolean))], [hunts]);
  const seasons = useMemo(() => [...new Set((hunts || []).map((h) => h.season).filter(Boolean))], [hunts]);
  const units = useMemo(() => [...new Set((hunts || []).map((h) => h.unit).filter(Boolean))], [hunts]);
  const photo = SPECIES_PHOTO[species] || SPECIES_PHOTO_FALLBACK;

  return (
    <div className="bg-paper min-h-screen" data-testid="species-page">
      <SiteHeader />

      <div className="max-w-[1200px] mx-auto px-5 sm:px-10 pt-4">
        <nav className="flex items-center gap-1.5 text-xs text-charcoal/50" aria-label="Breadcrumb" data-testid="species-breadcrumb">
          <Link to="/" className="hover:text-blaze">Home</Link><ChevronRight size={13} />
          <Link to="/explore" className="hover:text-blaze">States</Link><ChevronRight size={13} />
          <Link to={`/states/${encodeURIComponent(state)}`} className="hover:text-blaze">{state}</Link><ChevronRight size={13} />
          <span className="text-forest font-medium">{species}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative bg-charcoal text-bone overflow-hidden grain mt-4" data-testid="species-hero">
        <div className="absolute inset-0 z-0">
          <img src={photo} alt={species} className="w-full h-full object-cover object-center opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/40" />
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-5 sm:px-10 py-14">
          <span className="hr-overline text-blaze">{state} big game</span>
          <h1 className="font-serif text-4xl sm:text-6xl leading-[0.9] tracking-tight mt-2">{species} in {state}</h1>
          <p className="text-lg text-bone/80 mt-4 max-w-xl">Units, seasons, weapons and verified draw odds for {species.toLowerCase()} across {state}.</p>
          {system?.system_label && <span className="inline-flex items-center gap-2 text-sm text-bone/70 mt-4"><ShieldCheck size={15} className="text-blaze" /> {state} draw system: {system.system_label}</span>}
        </div>
      </section>

      {hunts === null || info === null ? (
        <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
      ) : (
        <main className="max-w-[1200px] mx-auto px-5 sm:px-10 py-12 space-y-10">
          {hunts.length === 0 ? (
            <div className="border border-dashed border-saddle/30 bg-white p-10 text-center" data-testid="species-empty">
              <p className="font-serif text-2xl text-forest">Verified {species} draw data for {state} is being collected.</p>
              <p className="text-charcoal/60 mt-2 max-w-lg mx-auto">We only publish official, verified numbers — no estimates. In the meantime, use the official {state} source for the latest {species.toLowerCase()} regulations and draw information.</p>
              {source && <a href={source} target="_blank" rel="noreferrer" className="text-blaze font-semibold text-sm mt-4 inline-flex items-center gap-1" data-testid="species-empty-source">View the official {state} source <ExternalLink size={13} /></a>}
              <div className="mt-6"><Link to={`/states/${encodeURIComponent(state)}`} className="inline-flex items-center gap-1.5 text-sm text-saddle hover:text-forest"><ChevronLeft size={15} /> Back to {state}</Link></div>
            </div>
          ) : (
            <>
              {/* Overview */}
              <section className="bg-white border border-saddle/20 p-6 grid grid-cols-2 sm:grid-cols-4 gap-6" data-testid="species-overview">
                <OverviewStat icon={Layers} label="Verified hunts" value={hunts.length} testid="ov-hunts" />
                <OverviewStat icon={MapPin} label={units.length === 1 ? "Unit" : "Units"} value={units.length} testid="ov-units" />
                <OverviewStat icon={Crosshair} label="Weapon types" value={weapons.length} testid="ov-weapons" />
                <OverviewStat icon={CalendarDays} label="Seasons" value={seasons.length} testid="ov-seasons" />
              </section>

              {/* Hunts */}
              <section data-testid="species-hunts">
                <h2 className="font-serif text-3xl text-charcoal tracking-tight">{species} hunts across {state}</h2>
                <div className="grid lg:grid-cols-2 gap-4 mt-5">
                  {hunts.map((h) => (
                    <HuntCard key={h.id} h={h} source={source} state={state} />
                  ))}
                </div>
              </section>

              <div><Link to={`/states/${encodeURIComponent(state)}`} className="inline-flex items-center gap-1.5 text-sm text-saddle hover:text-forest" data-testid="species-back"><ChevronLeft size={15} /> Back to all {state} species</Link></div>
            </>
          )}
        </main>
      )}

      <PolicyBar />
      <Footer />
    </div>
  );
}

function HuntCard({ h, source, state }) {
  const hist = h.odds_history || [];
  const year = h.season_year;
  const yl = year ? ` (${year})` : "";
  return (
    <div className="bg-white border border-saddle/20 p-5" data-testid={`species-hunt-${h.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-serif text-xl text-charcoal leading-tight">{h.unit}</h3>
          <p className="text-xs text-charcoal/55 mt-1">{HUNT_TYPE[h.draw_type] || "Draw"} · {h.weapon} · {h.residency} · {h.season}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4 pt-4 border-t border-saddle/12">
        <Metric label={`Draw odds${yl}`} value={`${h.draw_odds_pct}%`} accent testid="sp-odds" />
        <Metric label={`Avg points${yl}`} value={val(h.avg_points)} testid="sp-avg" />
        <Metric label="Creep (5 yr)" value={h.point_creep && h.point_creep !== "n/a" ? h.point_creep.replace(" pts/yr", "") : "—"} testid="sp-creep" />
        <Metric label={`Min points${yl}`} value={val(h.min_points)} testid="sp-min" />
        <Metric label={`Applicants${yl}`} value={h.applicants != null ? h.applicants.toLocaleString() : "—"} testid="sp-applicants" />
      </div>
      {hist.length > 1 && (
        <div className="h-28 mt-4 -ml-2" data-testid="sp-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={hist} margin={{ top: 6, right: 10, bottom: 0, left: 0 }}>
              <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#6b6b6b" }} axisLine={{ stroke: "#e2ddd2" }} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#6b6b6b" }} axisLine={false} tickLine={false} width={30} unit="%" domain={[0, (max) => Math.max(5, Math.ceil(max * 1.2))]} />
              <Tooltip formatter={(v) => [`${v}%`, "Draw odds"]} contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #e2ddd2" }} />
              <Line type="monotone" dataKey="pct" stroke="#D95D39" strokeWidth={2.5} dot={{ r: 2.5, fill: "#D95D39" }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="mt-4 pt-4 border-t border-saddle/12 flex items-center justify-between flex-wrap gap-2">
        <a href={`/marketplace?${new URLSearchParams({ species: h.species || "", weapon: h.weapon || "", hunt: `${state} ${h.species}` }).toString()}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-forest hover:text-blaze transition-colors" data-testid="sp-gear">
          <ShoppingBag size={14} /> Gear up
        </a>
        {(h.source_url || source) && (
          <a href={h.source_url || source} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-blaze hover:underline" data-testid="sp-source">
            Official source <ExternalLink size={13} />
          </a>
        )}
      </div>
    </div>
  );
}
